// The expanded row: plan, free grant, instance limit, language, notification
// preferences, security actions and delete.
//
// On the `components/ui` primitives: the panel is a Card, every filled action
// is a Button, every text-only action a LinkButton, the message strips are
// Alert, and each editable value is a Field so its label actually points at its
// control (the uppercase micro-labels used to be bare <label>s pointing at
// nothing). Inline editors therefore render at the standard control size.
//
// Every mutation here carries an onError now. They all used to fail silently:
// React Query kept the error, the panel showed nothing, and the admin was left
// looking at a value that had not changed.

import { useState } from 'react'
import type { ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { usersApi, type PlatformUser } from '../../api/client'
import { Alert, Badge, Button, Card, Field, Input, LinkButton, Select, cx } from '../../components/ui'
import { PLANS, capKey, extractApiError, planTone, supportedLanguages, type Plan } from './format'

const MICRO = 'block text-xs font-medium text-gray-500 uppercase tracking-wide'

/** The panel's uppercase micro-label, wearable inside a Field's <label>. */
function Micro({ children }: { children: ReactNode }) {
  return <span className={MICRO}>{children}</span>
}

const CloseIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
)

export function UserDetailPanel({ user, onDelete, deleting }: {
  user: PlatformUser
  onDelete: () => void
  deleting: boolean
}) {
  const instanceCount = user.instance_count ?? 0
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [editingLimit, setEditingLimit] = useState(false)
  const [limitVal, setLimitVal] = useState(user.instance_limit)
  const [editingPlan, setEditingPlan] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [inviteSent, setInviteSent] = useState(false)
  const [inviteError, setInviteError] = useState('')

  // One strip for the settings half of the panel (plan, grant, limit, language,
  // notifications). Security keeps its own, next to its own buttons.
  const [settingsError, setSettingsError] = useState('')
  const settingsErr = (fallbackKey: string, fallback?: string) => (e: unknown) =>
    setSettingsError(extractApiError(e) ?? t(fallbackKey, fallback ? { defaultValue: fallback } : undefined))

  const updateLimitMutation = useMutation({
    mutationFn: (limit: number) => usersApi.updateLimit(user.id, limit),
    onSuccess: () => {
      setSettingsError('')
      qc.invalidateQueries({ queryKey: ['users'] })
      setEditingLimit(false)
    },
    onError: settingsErr('users.limitUpdateError', 'Could not update the instance limit'),
  })

  const updatePlanMutation = useMutation({
    mutationFn: (plan: string) => usersApi.updatePlan(user.id, plan),
    onSuccess: () => {
      setSettingsError('')
      qc.invalidateQueries({ queryKey: ['users'] })
      setEditingPlan(false)
    },
    onError: settingsErr('users.planUpdateError', 'Could not update the plan'),
  })

  // Time-limited free grant (e.g. "1 year free"). Granting comps the customer:
  // it sets plan=paid (full features) AND the expiry in one call, so a free-tier
  // customer becomes a timed free-paid grant. Clearing just drops the expiry
  // (leaving them permanently paid — admin downgrades to free manually).
  const [editingGrant, setEditingGrant] = useState(false)
  const [grantDate, setGrantDate] = useState(user.plan_expires_at ? user.plan_expires_at.slice(0, 10) : '')
  const setGrantMutation = useMutation({
    mutationFn: (date: string) => usersApi.updatePlan(user.id, 'paid', date),
    onSuccess: () => {
      setSettingsError('')
      qc.invalidateQueries({ queryKey: ['users'] })
      setEditingGrant(false)
    },
    onError: settingsErr('users.grantUpdateError', 'Could not update the free grant'),
  })
  const clearGrantMutation = useMutation({
    mutationFn: () => usersApi.setPlanExpiry(user.id, ''),
    onSuccess: () => {
      setSettingsError('')
      qc.invalidateQueries({ queryKey: ['users'] })
      setEditingGrant(false)
    },
    onError: settingsErr('users.grantUpdateError', 'Could not update the free grant'),
  })

  // Customer identity (ünvan + phone) — the contact data the monthly BTK 5651
  // hosted-domain report needs. Saved together; the inputs are plain
  // controlled fields with an explicit save so a half-typed phone number is
  // never PATCHed on blur.
  const [companyVal, setCompanyVal] = useState(user.company_name ?? '')
  const [phoneVal, setPhoneVal] = useState(user.phone ?? '')
  const contactDirty = companyVal !== (user.company_name ?? '') || phoneVal !== (user.phone ?? '')
  const updateContactMutation = useMutation({
    mutationFn: () => usersApi.updateContact(user.id, { company_name: companyVal.trim(), phone: phoneVal.trim() }),
    onSuccess: () => {
      setSettingsError('')
      qc.invalidateQueries({ queryKey: ['users'] })
    },
    onError: settingsErr('users.contactUpdateError', 'Could not update the contact info'),
  })

  const updateLanguageMutation = useMutation({
    mutationFn: (language: string) => usersApi.updateLanguage(user.id, language),
    onSuccess: () => {
      setSettingsError('')
      qc.invalidateQueries({ queryKey: ['users'] })
    },
    onError: settingsErr('users.languageUpdateError', 'Could not update the language'),
  })

  const resendInviteMutation = useMutation({
    mutationFn: () => usersApi.resendInvite(user.id),
    onSuccess: () => {
      setInviteSent(true)
      setInviteError('')
      setTimeout(() => setInviteSent(false), 3000)
    },
    onError: (e: unknown) => {
      setInviteError(extractApiError(e) ?? t('users.resendError'))
      setTimeout(() => setInviteError(''), 5000)
    },
  })

  const updateNotificationsMutation = useMutation({
    mutationFn: (prefs: Partial<Pick<PlatformUser, 'notify_backup_failed' | 'notify_provision_failed' | 'notify_instance_ready' | 'marketing_emails'>>) =>
      usersApi.updateNotifications(user.id, prefs),
    onSuccess: () => {
      setSettingsError('')
      qc.invalidateQueries({ queryKey: ['users'] })
    },
    onError: settingsErr('notifications.saveFailed'),
  })

  const [securityMessage, setSecurityMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)
  const flashSecurity = (kind: 'success' | 'error', text: string) => {
    setSecurityMessage({ kind, text })
    setTimeout(() => setSecurityMessage(null), 4000)
  }
  const securityErr = (e: unknown, fallbackKey: string) => {
    flashSecurity('error', extractApiError(e) ?? t(fallbackKey))
  }

  const deactivateMutation = useMutation({
    mutationFn: () => usersApi.deactivate(user.id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); flashSecurity('success', t('users.security.deactivateSuccess')) },
    onError: (e) => securityErr(e, 'users.security.deactivateError'),
  })
  const activateMutation = useMutation({
    mutationFn: () => usersApi.activate(user.id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); flashSecurity('success', t('users.security.activateSuccess')) },
    onError: (e) => securityErr(e, 'users.security.activateError'),
  })
  const sendResetMutation = useMutation({
    mutationFn: () => usersApi.sendPasswordReset(user.id),
    onSuccess: () => flashSecurity('success', t('users.security.resetSentSuccess')),
    onError: (e) => securityErr(e, 'users.security.resetSentError'),
  })

  const [showSetPwd, setShowSetPwd] = useState(false)
  const [newPwd, setNewPwd] = useState('')
  const setPasswordMutation = useMutation({
    mutationFn: (pwd: string) => usersApi.setPassword(user.id, pwd),
    onSuccess: () => {
      flashSecurity('success', t('users.security.setPwdSuccess'))
      setShowSetPwd(false); setNewPwd('')
      qc.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (e) => securityErr(e, 'users.security.setPwdError'),
  })

  const userPlan = (user.plan ?? 'free') as Plan
  const userLang = supportedLanguages.find((l) => l.code === user.language) ?? supportedLanguages[0]

  return (
    <Card>
      {settingsError && <Alert tone="danger" className="mb-4">{settingsError}</Alert>}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <div>
          <p className={cx(MICRO, 'mb-2')}>{t('users.colPlan')}</p>
          {editingPlan ? (
            <div className="flex gap-1.5 flex-wrap items-center">
              {PLANS.map((p) => (
                <Button
                  key={p}
                  size="sm"
                  variant={userPlan === p ? 'primary' : 'secondary'}
                  disabled={updatePlanMutation.isPending}
                  onClick={() => updatePlanMutation.mutate(p)}
                >
                  {t(capKey('users.plan', p))}
                </Button>
              ))}
              <LinkButton tone="neutral" onClick={() => setEditingPlan(false)} aria-label={t('users.cancel')} className="p-1">
                <CloseIcon />
              </LinkButton>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setEditingPlan(true)}
              aria-label={t('users.planEditAria', { defaultValue: 'Change plan' })}
              className="rounded-full transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <Badge tone={planTone[userPlan]}>{t(capKey('users.plan', userPlan))}</Badge>
            </button>
          )}

          {/* Free grant — comp a customer for a fixed term (sets plan=paid + expiry) */}
          <div className="mt-3 pt-3 border-t border-gray-100">
            {editingGrant ? (
              <div className="flex items-end gap-1.5 flex-wrap">
                <Field label={<Micro>{t('users.grantLabel')}</Micro>}>
                  {(p) => (
                    <Input
                      {...p}
                      type="date"
                      fullWidth={false}
                      size="sm"
                      value={grantDate}
                      onChange={(e) => setGrantDate(e.target.value)}
                    />
                  )}
                </Field>
                <div className="flex items-center gap-1.5 pb-2.5">
                  <LinkButton
                    onClick={() => setGrantMutation.mutate(grantDate)}
                    disabled={setGrantMutation.isPending || !grantDate}
                  >
                    {t('users.save')}
                  </LinkButton>
                  {user.plan_expires_at && (
                    <LinkButton
                      tone="danger"
                      onClick={() => clearGrantMutation.mutate()}
                      disabled={clearGrantMutation.isPending}
                    >
                      {t('users.grantClear')}
                    </LinkButton>
                  )}
                  <LinkButton
                    tone="neutral"
                    aria-label={t('users.cancel')}
                    onClick={() => { setEditingGrant(false); setGrantDate(user.plan_expires_at ? user.plan_expires_at.slice(0, 10) : '') }}
                  >
                    <CloseIcon />
                  </LinkButton>
                </div>
              </div>
            ) : (
              <>
                <p className={cx(MICRO, 'mb-1.5')}>{t('users.grantLabel')}</p>
                {user.plan_expires_at ? (
                  <button
                    type="button"
                    onClick={() => setEditingGrant(true)}
                    title={t('users.grantEditHint')}
                    className="rounded-full transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                  >
                    <Badge tone="warning">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {t('users.grantUntil', { date: new Date(user.plan_expires_at).toLocaleDateString() })}
                    </Badge>
                  </button>
                ) : (
                  <LinkButton onClick={() => setEditingGrant(true)} className="inline-flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {t('users.grantSet')}
                  </LinkButton>
                )}
              </>
            )}
          </div>
        </div>

        <div>
          {editingLimit ? (
            <div className="flex items-end gap-2">
              <Field label={<Micro>{t('users.colLimit')}</Micro>}>
                {(p) => (
                  <Input
                    {...p}
                    type="number"
                    min={1}
                    max={100}
                    fullWidth={false}
                    size="sm"
                    className="w-20 text-center"
                    value={limitVal}
                    onChange={(e) => setLimitVal(Number(e.target.value))}
                  />
                )}
              </Field>
              <div className="flex items-center gap-2 pb-2.5">
                <LinkButton onClick={() => updateLimitMutation.mutate(limitVal)} disabled={updateLimitMutation.isPending}>
                  {t('users.save')}
                </LinkButton>
                <LinkButton
                  tone="neutral"
                  aria-label={t('users.cancel')}
                  onClick={() => { setEditingLimit(false); setLimitVal(user.instance_limit) }}
                >
                  <CloseIcon />
                </LinkButton>
              </div>
            </div>
          ) : (
            <>
              <p className={cx(MICRO, 'mb-2')}>{t('users.colLimit')}</p>
              <LinkButton tone="neutral" className="text-sm" onClick={() => setEditingLimit(true)}>
                {instanceCount} / {user.instance_limit}
                <span className="text-xs text-gray-400 ml-1.5">{t('users.edit')}</span>
              </LinkButton>
            </>
          )}
        </div>

        <div>
          <Field label={<Micro>{t('users.language')}</Micro>}>
            {(p) => (
              <Select
                {...p}
                fullWidth={false}
                size="sm"
                value={userLang.code}
                onChange={(e) => updateLanguageMutation.mutate(e.target.value)}
                disabled={updateLanguageMutation.isPending}
              >
                {supportedLanguages.map((l) => (
                  <option key={l.code} value={l.code}>{l.label}</option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      </div>

      {/* Contact info — company/trade name + phone (BTK 5651 domain report) */}
      <div className="mt-5 pt-4 border-t border-gray-100">
        <p className={cx(MICRO, 'mb-3')}>{t('users.contactTitle')}</p>
        <div className="flex flex-wrap items-end gap-3">
          <Field label={<Micro>{t('users.companyName')}</Micro>}>
            {(p) => (
              <Input
                {...p}
                type="text"
                fullWidth={false}
                size="sm"
                className="w-64"
                value={companyVal}
                onChange={(e) => setCompanyVal(e.target.value)}
                placeholder={t('users.companyNamePlaceholder')}
              />
            )}
          </Field>
          <Field label={<Micro>{t('users.phone')}</Micro>}>
            {(p) => (
              <Input
                {...p}
                type="tel"
                fullWidth={false}
                size="sm"
                className="w-44"
                value={phoneVal}
                onChange={(e) => setPhoneVal(e.target.value)}
                placeholder={t('users.phonePlaceholder')}
              />
            )}
          </Field>
          {contactDirty && (
            <div className="flex items-center gap-2 pb-2.5">
              <LinkButton
                onClick={() => updateContactMutation.mutate()}
                disabled={updateContactMutation.isPending}
              >
                {t('users.save')}
              </LinkButton>
              <LinkButton
                tone="neutral"
                aria-label={t('users.cancel')}
                onClick={() => { setCompanyVal(user.company_name ?? ''); setPhoneVal(user.phone ?? '') }}
              >
                <CloseIcon />
              </LinkButton>
            </div>
          )}
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-gray-100">
        <p className={cx(MICRO, 'mb-3')}>{t('notifications.title')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {([
            { key: 'notify_instance_ready' as const, label: t('notifications.instanceReady') },
            { key: 'notify_provision_failed' as const, label: t('notifications.provisionFailed') },
            { key: 'notify_backup_failed' as const, label: t('notifications.backupFailed') },
            { key: 'marketing_emails' as const, label: t('notifications.marketingEmails') },
          ] as const).map((pref) => (
            // A switch is not a labelable element, so the text is wired to it
            // with aria-label rather than a <label> that pointed at nothing.
            <div key={pref.key} className="flex items-center gap-3 group">
              <button
                type="button"
                role="switch"
                aria-checked={!!user[pref.key]}
                aria-label={pref.label}
                disabled={updateNotificationsMutation.isPending}
                onClick={() => updateNotificationsMutation.mutate({ [pref.key]: !user[pref.key] })}
                className={cx(
                  'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors',
                  'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1 disabled:opacity-50',
                  user[pref.key] ? 'bg-brand-600' : 'bg-gray-300',
                )}
              >
                <span
                  className={cx(
                    'pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transition-transform',
                    user[pref.key] ? 'translate-x-4' : 'translate-x-0',
                  )}
                />
              </button>
              <span className="text-xs text-gray-700 group-hover:text-gray-900">{pref.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Security & access controls — admin lock-out, password ops */}
      <div className="mt-5 pt-4 border-t border-gray-100">
        <p className={cx(MICRO, 'mb-3')}>{t('users.security.title')}</p>
        {securityMessage && (
          <Alert tone={securityMessage.kind === 'success' ? 'success' : 'danger'} className="mb-3">
            {securityMessage.text}
          </Alert>
        )}
        <div className="flex flex-wrap items-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => sendResetMutation.mutate()}
            disabled={user.status === 'deactivated'}
            loading={sendResetMutation.isPending}
            title={t('users.security.resetSentHint')}
          >
            {sendResetMutation.isPending ? t('users.security.sending') : t('users.security.sendReset')}
          </Button>

          {!showSetPwd ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => { setShowSetPwd(true); setNewPwd('') }}
              disabled={setPasswordMutation.isPending}
              title={t('users.security.setPwdHint')}
            >
              {t('users.security.setPwd')}
            </Button>
          ) : (
            <div className="flex items-end gap-2">
              <Field label={<Micro>{t('users.security.setPwdLabel', { defaultValue: 'New password' })}</Micro>}>
                {(p) => (
                  <Input
                    {...p}
                    type="text"
                    autoFocus
                    fullWidth={false}
                    size="sm"
                    className="w-56"
                    value={newPwd}
                    onChange={(e) => setNewPwd(e.target.value)}
                    placeholder={t('users.security.setPwdPlaceholder')}
                  />
                )}
              </Field>
              <Button
                size="sm"
                onClick={() => setPasswordMutation.mutate(newPwd)}
                disabled={newPwd.length < 8}
                loading={setPasswordMutation.isPending}
              >
                {setPasswordMutation.isPending ? t('users.security.setting') : t('users.security.setPwdConfirm')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => { setShowSetPwd(false); setNewPwd('') }}
                disabled={setPasswordMutation.isPending}
              >
                {t('users.cancel')}
              </Button>
            </div>
          )}

          <div className="flex-1" />

          {user.status === 'deactivated' ? (
            <Button
              variant="success"
              size="sm"
              onClick={() => activateMutation.mutate()}
              loading={activateMutation.isPending}
            >
              {activateMutation.isPending ? t('users.security.activating') : t('users.security.activate')}
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => deactivateMutation.mutate()}
              loading={deactivateMutation.isPending}
              title={t('users.security.deactivateHint')}
            >
              {deactivateMutation.isPending ? t('users.security.deactivating') : t('users.security.deactivate')}
            </Button>
          )}
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-gray-100 flex items-center gap-3 flex-wrap">
        {user.status === 'pending' && (
          <Button
            variant={inviteSent ? 'success' : 'secondary'}
            size="sm"
            onClick={() => resendInviteMutation.mutate()}
            disabled={inviteSent}
            loading={resendInviteMutation.isPending}
            title={inviteError || t('users.resendInviteHint')}
          >
            {resendInviteMutation.isPending
              ? t('users.sending', { defaultValue: 'Sending...' })
              : inviteSent
                ? t('users.inviteSent', { defaultValue: 'Sent!' })
                : inviteError
                  ? t('users.resendFailed')
                  : t('users.resendInvite')}
          </Button>
        )}
        {inviteError && <span className="text-xs text-red-600">{inviteError}</span>}

        <div className="flex-1" />

        {confirmDelete ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-red-600 font-medium">
              {t('users.confirmDelete', { defaultValue: 'Are you sure?' })}
            </span>
            <Button
              variant="danger"
              size="sm"
              onClick={() => { onDelete(); setConfirmDelete(false) }}
              loading={deleting}
            >
              {deleting
                ? t('users.deleting', { defaultValue: 'Deleting...' })
                : t('users.confirmYes', { defaultValue: 'Yes, delete' })}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setConfirmDelete(false)}>
              {t('users.cancel')}
            </Button>
          </div>
        ) : (
          <LinkButton tone="danger" onClick={() => setConfirmDelete(true)} className="px-3 py-1.5 rounded-lg hover:bg-red-50">
            {t('users.delete')}
          </LinkButton>
        )}
      </div>
    </Card>
  )
}
